/* eslint-disable @typescript-eslint/no-unused-vars */
import { Capacitor } from '@capacitor/core';
import { Purchases, LOG_LEVEL, PACKAGE_TYPE, PurchasesPackage } from '@revenuecat/purchases-capacitor';

export interface IOSPurchaseResult {
  customerInfo: any;
  productIdentifier: string;
  success: boolean;
  outcome?: 'cancelled' | 'pending' | 'error';
}

class IOSPaymentService {
  private isInitialized = false;

  async initialize(): Promise<void> {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
      console.log('🍎 IOSPaymentService: Not on iOS platform, skipping initialization');
      return;
    }

    if (this.isInitialized) {
      console.log('🍎 IOSPaymentService: Already initialized');
      return;
    }

    try {
      // Initialize RevenueCat with your public iOS SDK key
      // You'll need to get this from RevenueCat dashboard after creating an account
      const IOS_SDK_KEY = 'appl_kqexBbLUcctkGiHIPdHzWaqoETq';
      
      await Purchases.configure({
        apiKey: IOS_SDK_KEY,
        appUserID: undefined, // Will be set when user logs in
      });

      await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
      
      this.isInitialized = true;
      console.log('🍎 IOSPaymentService: Initialized successfully');
    } catch (error) {
      console.error('🍎 IOSPaymentService: Initialization failed:', error);
      throw error;
    }
  }

  async setUserId(userId: string): Promise<void> {
    if (!userId.trim()) throw new Error('Sign in before using App Store purchases.');
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      await Purchases.logIn({ appUserID: userId });
      console.log('🍎 IOSPaymentService: User ID set:', userId);
    } catch (error) {
      console.error('🍎 IOSPaymentService: Failed to set user ID:', error);
      throw error;
    }
  }

  async getAvailablePackages(): Promise<PurchasesPackage[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const offerings = await Purchases.getOfferings();
      console.log('🍎 IOSPaymentService: Available offerings:', offerings);

      if (offerings.current) {
        const packages = offerings.current.availablePackages || [];
        console.log('🍎 IOSPaymentService: Available packages:', packages);
        return packages;
      }

      return [];
    } catch (error) {
      console.error('🍎 IOSPaymentService: Failed to get packages:', error);
      return [];
    }
  }

  async purchasePackage(packageToPurchase: PurchasesPackage): Promise<IOSPurchaseResult> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      console.log('🍎 IOSPaymentService: Starting purchase for package:', packageToPurchase.identifier);
      
      const result = await Purchases.purchasePackage({ 
        aPackage: packageToPurchase 
      });

      console.log('🍎 IOSPaymentService: Purchase result:', result);

      return {
        customerInfo: result.customerInfo,
        productIdentifier: packageToPurchase.product.identifier,
        success: true
      };
    } catch (error) {
      console.error('🍎 IOSPaymentService: Purchase failed:', error);
      return {
        customerInfo: null,
        productIdentifier: packageToPurchase.product.identifier,
        success: false,
        outcome: String((error as any)?.code) === '1' || (error as any)?.userCancelled ? 'cancelled' : String((error as any)?.code) === '20' ? 'pending' : 'error'
      };
    }
  }

  async restorePurchases(userId: string): Promise<any> {
    // A restore must belong to the signed-in FinFlow account, not a cached
    // anonymous ID or the previous account used on this device.
    await this.setUserId(userId);

    try {
      console.log('🍎 IOSPaymentService: Restoring purchases...');
      const { customerInfo } = await Purchases.restorePurchases();
      console.log('🍎 IOSPaymentService: Purchases restored:', customerInfo);
      return customerInfo;
    } catch (error) {
      console.error('🍎 IOSPaymentService: Failed to restore purchases:', error);
      throw error;
    }
  }

  async getCustomerInfo(): Promise<any> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const { customerInfo } = await Purchases.getCustomerInfo();
      console.log('🍎 IOSPaymentService: Customer info:', customerInfo);
      return customerInfo;
    } catch (error) {
      console.error('🍎 IOSPaymentService: Failed to get customer info:', error);
      return null;
    }
  }

  async checkPremiumStatus(): Promise<'free' | 'premium'> {
    try {
      const customerInfo = await this.getCustomerInfo();
      
      if (customerInfo?.entitlements?.active?.premium) {
        console.log('🍎 IOSPaymentService: User has premium entitlement');
        return 'premium';
      }

      console.log('🍎 IOSPaymentService: User does not have premium entitlement');
      return 'free';
    } catch (error) {
      console.error('🍎 IOSPaymentService: Failed to check premium status:', error);
      return 'free';
    }
  }

  isAvailable(): boolean {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  }
}

export const iosPaymentService = new IOSPaymentService();
export default iosPaymentService;
